import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Slider, Select, Space, message, Popconfirm, Row, Col } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import OfficeLocationPicker from "../../components/OfficeLocationPicker";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import { timeZoneOptions, tzLabel } from "../../helper/timezone";

const TIMEZONES = timeZoneOptions();

const Offices = () => {
  const { request } = useRequest();
  const [offices, setOffices] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [lat, setLat] = useState(28.6139);
  const [lng, setLng] = useState(77.209);
  const [radius, setRadius] = useState(150);
  const [geocoding, setGeocoding] = useState(false);
  const [form] = Form.useForm();

  // Address -> coordinates via OpenStreetMap's free Nominatim geocoder — same
  // no-API-key stack as the map itself. Typing an address alone never moved
  // the pin before; this is what actually connects the two.
  const handleGeocodeAddress = async (address) => {
    if (!address || !address.trim()) {
      message.warning("Enter an address to search first");
      return;
    }
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(address)}`
      );
      const results = await res.json();
      if (!results.length) {
        message.error("Could not find that address — try being more specific, or click the map directly");
        return;
      }
      const found = results[0];
      setLat(parseFloat(found.lat));
      setLng(parseFloat(found.lon));
      message.success(`Located: ${found.display_name}`);
    } catch (err) {
      message.error("Address search failed — check your connection and try again");
    } finally {
      setGeocoding(false);
    }
  };

  const fetchOffices = () => {
    request({ url: apiPath.offices, method: "GET", onSuccess: (res) => setOffices(res?.data?.data || []) });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(fetchOffices, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setLat(28.6139);
    setLng(77.209);
    setRadius(150);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setLat(record.lat);
    setLng(record.lng);
    setRadius(record.geofenceRadiusMeters);
    setModalOpen(true);
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, lat, lng, geofenceRadiusMeters: radius };
      const url = editing ? `${apiPath.offices}/${editing._id}` : apiPath.offices;
      const method = editing ? "PUT" : "POST";
      request({
        url,
        method,
        data: payload,
        onSuccess: () => {
          message.success(editing ? "Office updated" : "Office created");
          setModalOpen(false);
          fetchOffices();
        },
        onError: (err) => message.error(err?.response?.data?.message || "Save failed"),
      });
    });
  };

  const onDelete = (record) => {
    request({
      url: `${apiPath.offices}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Office deleted");
        fetchOffices();
      },
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Address", dataIndex: "address" },
    { title: "Lat", dataIndex: "lat" },
    { title: "Lng", dataIndex: "lng" },
    { title: "Geofence Radius (m)", dataIndex: "geofenceRadiusMeters" },
    { title: "Timezone", dataIndex: "timezone", render: (v) => tzLabel(v) },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Delete this office?" onConfirm={() => onDelete(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Offices"
        subtitle="Manage office locations and geofence boundaries"
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Office
          </Button>
        }
      />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={offices}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No offices added yet" actionLabel="Add Office" onAction={openCreate} /> }}
      />

      <Modal title={editing ? "Edit Office" : "Add Office"} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={onSubmit} width={720} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="timezone" label="Timezone" initialValue="Asia/Manila" rules={[{ required: true }]} help="Attendance dates, shift times and late/early checks follow this timezone.">
            <Select showSearch options={TIMEZONES} optionFilterProp="label" getPopupContainer={() => document.body} />
          </Form.Item>
          <Form.Item name="address" label="Address" help="Search moves the map — you can still fine-tune by clicking, or use your current location.">
            <Input.Search
              placeholder="e.g. Connaught Place, New Delhi"
              enterButton={<SearchOutlined />}
              loading={geocoding}
              onSearch={handleGeocodeAddress}
            />
          </Form.Item>
        </Form>
        <p style={{ marginBottom: 8 }}>Click on the map to fine-tune the exact pin, or search an address above:</p>
        <div style={{ borderRadius: "var(--i-ems-radius)", overflow: "hidden", boxShadow: "var(--i-ems-shadow-sm)", border: "1px solid var(--i-ems-border)" }}>
          <OfficeLocationPicker lat={lat} lng={lng} onChange={(newLat, newLng) => { setLat(newLat); setLng(newLng); }} />
        </div>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={8}>
            <label>Latitude</label>
            <InputNumber style={{ width: "100%" }} value={lat} onChange={setLat} step={0.0001} />
          </Col>
          <Col span={8}>
            <label>Longitude</label>
            <InputNumber style={{ width: "100%" }} value={lng} onChange={setLng} step={0.0001} />
          </Col>
          <Col span={8}>
            <label>Geofence Radius: {radius >= 1000 ? `${(radius / 1000).toFixed(1)}km` : `${radius}m`}</label>
            <Slider min={10} max={3000} step={10} value={radius} onChange={setRadius} />
          </Col>
        </Row>
      </Modal>
    </PageTransition>
  );
};

export default Offices;
